import { NextRouter } from "next/router";
import { productAs, productHref } from "entity/product/helpers/linkProps";
import { categoryAs, categoryHref } from "entity/category/helpers/linkProps";
import { teamAs, teamHref } from "entity/person/helpers/linkProps";
import { articleAs, articleHref } from "entity/article/helpers/linkProps";
import { pharmacyHref, pharmacyAs } from "helpers/rules/pharmacy";
import { suggestionAs, suggestionHref } from "helpers/rules/suggestion";
import { navLinkAs, navLinkHref } from "helpers/rules/navLink";
import { searchAs, searchHref } from "helpers/rules/search";
import { letterAs, letterHref } from "helpers/rules/letter";
import { partnerAs, partnerHref } from "helpers/rules/partner";
import { brandAs, brandHref } from "helpers/rules/brand";
import { groupAs, groupHref } from "helpers/rules/group";
import {
  productAnalogsAs,
  productAnalogsHref,
} from "helpers/rules/productAnalogs";
import { discountsAs, discountsHref } from "helpers/rules/discounts";
import { promoAs, promoHref } from "helpers/rules/promo";
import { analogiAs, analogiHref } from "helpers/rules/analogi";
import { UrlObject } from "url";

export type EntityInput = Record<string, any>; // todo: use real entity types
export type LinkPropsOptions = {
  withOrigin?: boolean;
  withRegion?: boolean;
};
export type LinkProps = {
  href: string | Partial<UrlObject>;
  as: string | Partial<UrlObject>;
  prefetch?: boolean;
};

type EntityHrefOptions = Partial<{ origin: string; regionSegment: string }>;

export function getEntityHref(
  type: string,
  entity: EntityInput,
  { origin, regionSegment: region }: EntityHrefOptions = {
    origin: "",
    regionSegment: "",
  }
): LinkProps["href"] {
  const obj: LinkProps["href"] = { pathname: "/" };

  switch (type) {
    case "pharmacy":
      return pharmacyHref(entity);
    case "category":
      return categoryHref(entity, region);
    case "suggestion":
      return suggestionHref(entity, region, origin);
    case "product":
      return productHref(entity);
    case "navLinks":
      return navLinkHref(entity, region, origin);
    case "search":
      return searchHref(entity, region);
    case "letter":
      return letterHref(entity, region);
    case "partner": // maybe deprecated
      return partnerHref(entity, region, origin);
    case "brand":
      return brandHref();
    case "article":
      return articleHref(entity, origin);
    case "productAnalogs":
      return productAnalogsHref(entity, origin);
    case "discounts":
      return discountsHref(entity, region);
    case "promo":
      return promoHref(entity);
    case "team":
      return teamHref(entity);
    case "group":
      return groupHref(entity, region, origin);
    case "analogi":
      return analogiHref(entity, origin);
    default:
      return obj;
  }
}

export function getEntityAs(
  type: string,
  entity: EntityInput,
  { origin, regionSegment }: EntityHrefOptions = {
    origin: "",
    regionSegment: "",
  }
): LinkProps["as"] {
  const region = regionSegment;

  switch (type) {
    case "pharmacy":
      return pharmacyAs(entity);
    case "category":
      return categoryAs(entity, region);
    case "suggestion":
      return suggestionAs(entity, region, origin);
    case "product":
      return productAs(entity);
    case "navLinks":
      return navLinkAs(entity, region, origin);
    case "search":
      return searchAs(entity, region);
    case "letter":
      return letterAs(entity, region);
    case "partner": // maybe deprecated
      return partnerAs(entity, region, origin);
    case "brand":
      return brandAs(entity);
    case "article":
      return articleAs(entity, origin);
    case "productAnalogs":
      return productAnalogsAs(entity, origin);
    case "discounts":
      return discountsAs(entity, region);
    case "promo":
      return promoAs(entity);
    case "team":
      return teamAs(entity);
    case "group":
      return groupAs(entity, region, origin);
    case "analogi":
      return analogiAs(entity, origin);
    default:
      return "/";
  }
}

export function isURLAbsolute(url = ""): boolean {
  return (
    String(url).startsWith("https://") ||
    String(url).startsWith("http://") ||
    String(url).startsWith("//") // for non-scheme urls
  );
}

export function isURLRegional(url: string): boolean {
  return /(\/r_[^\\/]*)/.test(url);
}

export function getCanonical(
  host: string,
  router: Pick<NextRouter, "asPath" | "pathname">,
  removeRegion = false
): string {
  let path = router.asPath;
  const pathname = router.pathname;
  const hasRegion = isURLRegional(path);
  const hasFilters =
    pathname.includes("/[filters]") || /([^/]+:[^/]*)/.test(path);
  path = path.split("?")[0];

  if (hasFilters || hasRegion) {
    if (hasFilters) {
      path = path.replace(/\/[^\\/]+:[^\\/]+/, "");
    }

    if (hasRegion && removeRegion) {
      path = path.replace(/(\/r_[^\\/]*)/, "");
    }

    /* Clear doubled or more slashes */
    path = path.replace(/\/{2,}/g, "/");
  }

  if (path.endsWith(".html/")) {
    path = path.replace(".html/", ".html");
  }

  return `${host}${path}`;
}

export type GetUrlUtmObjResult = { [key: string]: string };
export const getUrlUtmObj = (url: string): GetUrlUtmObjResult => {
  if (!url || typeof url !== "string") return;
  if (!isURLAbsolute(url)) return;

  const utms = Array.from(new URL(url).searchParams).filter(n =>
    n[0].startsWith("utm_")
  );

  if (!utms || !utms.length) return;

  return utms.reduce((acc, n) => ({ ...acc, [n[0]]: n[1] }), {});
};

type GetUrlOptions = {
  url: string;
};
type GetUrlQueries = {
  [key: string | number]: unknown;
};
export const getUrlQueries =
  ({ url }: GetUrlOptions = { url: "" }) =>
  (queries: GetUrlQueries = {}) => {
    let optionUrl = "";
    for (const key in queries) {
      optionUrl += optionUrl
        ? `&${key}=${queries[key]}`
        : `${key}=${queries[key]}`;
    }

    return url ? (optionUrl ? url + `/?${optionUrl}` : url) : optionUrl;
  };

export function getUrlDetails(url: string): Partial<URL> {
  if (!url || typeof url !== "string") return;
  if (!isURLAbsolute(url)) return;

  return new URL(url);
}

export function withTrailSlash(value: string): string {
  if (!value) return "";
  return `${value}${!value.endsWith("/") ? "/" : ""}`;
}
